/**
 * PriceRangeInput — the сом bracket the buyer can afford (STORY-003-005;
 * rebuilt 2026-08).
 *
 * WHAT WAS WRONG. Two bare boxes placeholdered "от" and "до", with a dash
 * between them and no unit anywhere. A placeholder is not a label: it vanishes
 * the moment the field has content, so a user who typed 5000 into the left box
 * and came back a minute later had no way to tell which bound they had set.
 * There was no сом anywhere near the numbers, in a country where the same
 * container quotes in сом and the neighbouring one in dollars. And min > max —
 * the one input error this control can produce — silently returned an empty
 * catalogue with no explanation, which reads to a first-time buyer as "there is
 * nothing here" rather than "you asked for nothing".
 *
 * WHAT IT IS NOW. Two labelled fields with the unit set INSIDE the field as a
 * suffix, the figures in `.g-data` (a price bound is a reading, so it belongs
 * in the instrument face and its digits should be tabular), and a live
 * validation line that names the actual problem. The invalid range is still
 * pushed to the URL — the URL must stay a truthful description of what is on
 * screen, and the grid's empty state plus this message together explain the
 * blank result instead of leaving it mysterious.
 *
 * Values are sent to the API as DECIMAL сом directly (`min_price` / `max_price`)
 * — NO tiyin conversion, because prices are `Numeric(12,2)` decimal KGS (frozen
 * schema STORY-000-005; ADR-031). An empty input maps to `null` (no bound).
 *
 * Tokens via the `--g-*` mirror; strings from the `marketplace` namespace.
 */
import { useId, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";

import { iconProps } from "@/lib/icons";

export interface PriceRangeValue {
  min: number | null;
  max: number | null;
}

export interface PriceRangeInputProps {
  value: PriceRangeValue;
  onChange: (value: PriceRangeValue) => void;
}

/** Parse a raw input string to a non-negative number, or null when empty/invalid. */
function parsePrice(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
}

interface BoundFieldProps {
  id: string;
  label: string;
  unit: string;
  testId: string;
  value: number | null;
  invalid: boolean;
  describedBy?: string;
  onChange: (next: number | null) => void;
}

/** One bound: a real `<label>`, a mono figure, and the unit inside the field. */
function BoundField({
  id,
  label,
  unit,
  testId,
  value,
  invalid,
  describedBy,
  onChange,
}: BoundFieldProps): ReactElement {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
      <label
        htmlFor={id}
        className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-[color:var(--g-ink-2)]"
      >
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type="number"
          inputMode="numeric"
          min={0}
          step="0.01"
          data-testid={testId}
          value={value ?? ""}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          onChange={(e) => onChange(parsePrice(e.target.value))}
          // `pr-11` keeps the caret clear of the unit; the sunken well is the
          // third surface step, so an input reads as recessed rather than as
          // another raised card.
          className={
            "g-data h-11 w-full rounded-[10px] border bg-[color:var(--g-sunken)] px-3 pr-11 text-sm text-[color:var(--g-figure)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background " +
            (invalid
              ? "border-[color:var(--g-error)]"
              : "border-[color:var(--g-rule)]")
          }
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-[color:var(--g-ink-2)]"
        >
          {unit}
        </span>
      </div>
    </div>
  );
}

export function PriceRangeInput({
  value,
  onChange,
}: PriceRangeInputProps): ReactElement {
  const { t } = useTranslation("marketplace");
  const minId = useId();
  const maxId = useId();
  const errorId = useId();

  const invalid =
    value.min !== null && value.max !== null && value.min > value.max;
  const unit = t("card.currency");

  return (
    <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
      {/* The group needs its own accessible name; the sidebar heading above is
          a sibling, not an ancestor, so a screen reader would otherwise read
          two unlabelled spinbuttons. */}
      <legend className="sr-only">{t("filters.priceRange")}</legend>
      <div className="flex items-end gap-2">
        <BoundField
          id={minId}
          label={t("filters.priceMin")}
          unit={unit}
          testId="price-min"
          value={value.min}
          invalid={invalid}
          describedBy={invalid ? errorId : undefined}
          onChange={(min) => onChange({ min, max: value.max })}
        />
        <BoundField
          id={maxId}
          label={t("filters.priceMax")}
          unit={unit}
          testId="price-max"
          value={value.max}
          invalid={invalid}
          describedBy={invalid ? errorId : undefined}
          onChange={(max) => onChange({ min: value.min, max })}
        />
      </div>
      {invalid && (
        <p
          id={errorId}
          role="alert"
          data-testid="price-range-error"
          className="flex items-start gap-1.5 text-xs leading-snug text-[color:var(--g-error)]"
        >
          <AlertTriangle {...iconProps("xs")} className="mt-0.5 shrink-0" />
          {t("filters.priceRangeInvalid")}
        </p>
      )}
    </fieldset>
  );
}
